// Accordion labels switched to sentence case (acronyms kept).
import React, { useEffect, useMemo, useState } from 'react';

import { AccessSummaryTypes, NativeResources } from '@ariksa/inventory-core';
import {
  ActionsResponse,
  CanAccessSummary,
  UsageFilter,
} from '@ariksa/inventory-core/api';
import { Accordion, Box, Center, Flex, HStack, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { Tag } from 'components/DataDisplay';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { NoDataAvailable } from 'components/DataDisplay/Utils/NoDataAvailable';
import { OutlinedIconButton, Select } from 'components/DataEntry';
import { ButtonGroupItem } from 'components/DataEntry/Button/ButtonGroup';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { HighPrivilegesIcon, IconTypes, PolicyIcon } from 'components/Icons';
import {
  AccessScopeButtons,
  AccountCell,
  useAccessScope,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AccessScope';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { PermissionDetails } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/PermissionDetails';
import {
  PermissionList,
  UsageLegend,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/PermissionList';
import { PermissionUsageCheckbox } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/PermissionUsageCheckbox';
import {
  getPrivilegeCounts,
  permissionEntries,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/permissionUtils';
import {
  createPermissionEntityRenderer,
  getAccessPathSummary,
  getIdentityPermissionColumns,
  getPathFilter,
  isNarrowable,
  noPolicyType,
  policyTypeMapping,
  snowflakeIdentities,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/utils';
import {
  getHighPrivilegeColor,
  renderHighPrivilege,
  renderHighPrivilegesCell,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useRedirectToGraph } from 'containers/ActiveCloudResource/hooks/useRedirectToGraph';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';

export const IdentityPermissions = () => {
  const dispatch = useDispatch();
  const {
    resourceUuid,
    resourceAccountId,
    resourceType,
    permissionsMap,
  } = useSelector(selectActiveResource);
  const [permissionType, setPermissionType] = useState('');
  const [policyType, setPolicyType] = useState<Record<string, any>>(
    noPolicyType,
  );
  const [selectedPrivilege, setSelectedPrivilege] = useState<string>();
  const [search, setSearch] = useState('');
  // The access path the permissions below are narrowed to, if any.
  const [selectedPath, setSelectedPath] = useState<CanAccessSummary>();
  const { getTabIndex, hasTab } = useMetadataDrawerTabs();
  const { onOpenMetadata } = useActiveResourceContext();
  const { canAccessSummary } = useSelector(selectActiveResource);
  const { canAccess } = canAccessSummary;
  const { getCanAccessSummary } = useActiveResourceActions();
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  const { redirectToGraph } = useRedirectToGraph();

  const [lastResponse, setLastResponse] = useState<{
    resourceUuid: string;
    data: ActionsResponse;
  }>();

  const isSnowflake = snowflakeIdentities.includes(resourceType);
  const showPermissionDetails = resourceType !== NativeResources.IamPolicy;
  const showExternalResources = !isSnowflake;

  useEffect(() => {
    getCanAccessSummary(AccessSummaryTypes.CanAccess);
  }, [getCanAccessSummary]);

  const paths = useMemo(
    () => ((canAccess.data ?? []) as unknown) as CanAccessSummary[],
    [canAccess.data],
  );

  const {
    scope,
    setScope,
    scopedRows: scopedPaths,
    scopeItems,
    hasExternal,
  } = useAccessScope(paths);

  // filters, selection and page belong to one entity only
  useEffect(() => {
    setSelectedPrivilege(undefined);
    setPermissionType('');
    setPolicyType(noPolicyType);
    setLastResponse(undefined);
    setSearch('');
    setScope('all');
    setSelectedPath(undefined);
  }, [resourceUuid, resourceType, setScope]);

  useEffect(() => {
    if (permissionsMap.isSuccess) {
      setLastResponse({ resourceUuid, data: permissionsMap.data });
    }
  }, [permissionsMap.isSuccess, permissionsMap.data, resourceUuid]);

  // The API returns the counts for every privilege alongside the whole of the
  // selected one, so selection and filters are the only request parameters.
  useEffect(() => {
    if (!showPermissionDetails) return;
    dispatch(
      actions.getCrudPermissions({
        q: {
          actionsRequest: {
            uuids: [resourceUuid],
            native_type: resourceType,
            account_id: resourceAccountId,
            select_crud_level: selectedPrivilege,
            granted_by: policyType?.value,
            usage: permissionType ? (permissionType as UsageFilter) : undefined,
            ...getPathFilter(selectedPath),
          },
        },
      }),
    );
  }, [
    dispatch,
    resourceAccountId,
    resourceType,
    resourceUuid,
    showPermissionDetails,
    selectedPrivilege,
    policyType,
    permissionType,
    selectedPath,
  ]);

  // A pending request clears the store's data, so fall back to the last
  // successful response to hold the selector and filters steady while the next
  // privilege loads. The rows and counts it describes are the previous
  // request's, so the body below shows a spinner rather than that stale answer.
  const isLoadingPermissions = permissionsMap.isLoading;
  const actionsResponse: ActionsResponse =
    isLoadingPermissions && lastResponse?.resourceUuid === resourceUuid
      ? lastResponse.data
      : permissionsMap.data;

  // The API returns one crud level at a time, and names it on the group.
  const activeLevel = actionsResponse?.permissions?.[0];

  const privilegeCounts = useMemo(
    () => getPrivilegeCounts(actionsResponse?.crud_level_counts),
    [actionsResponse],
  );

  /**
   * Until a privilege is picked the API chooses one and echoes it back, so the
   * selector can highlight it without spending an extra request.
   *
   * A picked privilege holds only while the response still counts it. The
   * filters can empty the level that was picked, and the API then answers with
   * a different one; following the click would leave the selector, the count
   * and the high-privilege marker describing a level the table is not showing.
   */
  const activePrivilege =
    selectedPrivilege &&
    privilegeCounts.some(p => p.crudLevel === selectedPrivilege)
      ? selectedPrivilege
      : activeLevel?.crud_level;

  const supportsUsage = !!actionsResponse?.supports_usage;

  // `used` is null where the provider reports no usage at all, which is not
  // the same claim as "not used". The legend names that third state only when
  // the response actually contains one.
  const hasUnknownUsage = useMemo(
    () =>
      (activeLevel?.permissions_by_native ?? []).some(group =>
        permissionEntries(group).some(([, used]) => used == null),
      ),
    [activeLevel],
  );

  const pathSummary = useMemo(() => getAccessPathSummary(scopedPaths), [
    scopedPaths,
  ]);

  // Selecting the row already showing sends the list back to every path. A row
  // the request cannot be narrowed by is not selectable, so a click never
  // leaves the permissions below unchanged with no reason given.
  const selectPath = (row: CanAccessSummary) => {
    if (!isNarrowable(row)) return;
    setSelectedPath(current =>
      current?.permission_entity_uuid === row?.permission_entity_uuid
        ? undefined
        : row,
    );
  };

  const selectedCount = useMemo(() => {
    const selected = privilegeCounts.find(p => p.crudLevel === activePrivilege);
    if (!selected) return '';
    return `${selected.count.toLocaleString()} out of ${(
      actionsResponse?.total ?? 0
    ).toLocaleString()} permissions`;
  }, [actionsResponse, privilegeCounts, activePrivilege]);

  const isSelectedHighPrivilege = !!privilegeCounts.find(
    p => p.crudLevel === activePrivilege,
  )?.isHighPrivilege;

  const policyTypeOptions = useMemo(
    () => [
      noPolicyType,
      ...(actionsResponse?.policy_types ?? []).map(p => ({
        label: policyTypeMapping(p),
        value: p,
      })),
    ],
    [actionsResponse],
  );
  const supportsPolicyType = policyTypeOptions.length > 1;

  // high privilege is a property of the crud level, so it is marked once per
  // privilege. The marker slot always renders so labels stay aligned.
  const privilegeItems: ButtonGroupItem[] = useMemo(
    () =>
      privilegeCounts.map(p => ({
        label: (
          <HStack spacing={1.5}>
            {p.isHighPrivilege &&
              renderHighPrivilege(p.label, getHighPrivilegeColor(p.crudLevel))}
            <Box>{`${p.label}: ${p.count.toLocaleString()}`}</Box>
          </HStack>
        ),
        value: p.crudLevel,
      })),
    [privilegeCounts],
  );

  const renderEntity = useMemo(
    () =>
      createPermissionEntityRenderer(getCloudNativeName, getCloudAgnosticName),
    [getCloudNativeName, getCloudAgnosticName],
  );

  const accessPathColumns = useMemo(
    () =>
      getIdentityPermissionColumns({
        isSnowflake,
        renderEntity,
        AccountCell,
        renderHighPrivilegesCell,
      }),
    [isSnowflake, renderEntity],
  );

  // the access tab is the entity level policy destination; not every identity
  // type exposes it, and this action must never depend on the privilege
  const showPolicies = hasTab(resourceType, MetadataTabs.Access);

  // the table sizes itself against its container, so the detail area needs a
  // definite height now that it no longer sits inside an accordion panel
  const permissionPanel = (
    <Flex flex={1} h="full" w="full">
      {!!privilegeItems.length ? (
        <Stack h="full" w="full" minH={0} spacing={0}>
          {(supportsUsage ||
            supportsPolicyType ||
            showPolicies ||
            isSelectedHighPrivilege) && (
            <HStack justify="space-between" w="full">
              <HStack spacing={2}>
                <Box w={72}>
                  <SearchBox
                    value={search}
                    onChange={setSearch}
                    placeholder="Search permission"
                    showShortcut={false}
                  />
                </Box>
                {supportsPolicyType && (
                  <HStack>
                    <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                    <Box>
                      <Select
                        options={policyTypeOptions}
                        value={policyType}
                        onChange={value => setPolicyType(value)}
                        showIconInValueContainer
                        menuPortalTarget={document.body}
                        isDisabled={permissionsMap.isLoading}
                      />
                    </Box>
                  </HStack>
                )}
                {supportsUsage && (
                  <HStack spacing={4}>
                    <FilterLabelWrapper>List</FilterLabelWrapper>
                    <PermissionUsageCheckbox
                      value="used"
                      label="Used"
                      selectedValue={permissionType}
                      onChange={setPermissionType}
                    />
                    <PermissionUsageCheckbox
                      value="excess"
                      label="Excess"
                      selectedValue={permissionType}
                      onChange={setPermissionType}
                    />
                  </HStack>
                )}
                {isSelectedHighPrivilege && (
                  <Box>
                    <Tag
                      label="High Privilege"
                      leftIcon={
                        <Center boxSize={4} color="medium">
                          <HighPrivilegesIcon />
                        </Center>
                      }
                    />
                  </Box>
                )}
              </HStack>
              <HStack spacing={3}>
                {showPolicies && (
                  <OutlinedIconButton
                    leftIcon={<PolicyIcon />}
                    onClick={() =>
                      onOpenMetadata(
                        getTabIndex(resourceType, MetadataTabs.Access),
                      )
                    }
                  >
                    Show Policies
                  </OutlinedIconButton>
                )}
              </HStack>
            </HStack>
          )}

          <Stack flex={1} h="full" w="full" minW={0} spacing={2}>
            <HStack justify="flex-end" align="baseline">
              <Box fontWeight={400}>{selectedCount}</Box>
              {supportsUsage && <UsageLegend showUnknown={hasUnknownUsage} />}
            </HStack>
            <PermissionList
              level={activeLevel}
              search={search}
              isLoading={isLoadingPermissions}
              supportsUsage={supportsUsage}
              supportsPolicyType={supportsPolicyType}
            />
          </Stack>
        </Stack>
      ) : (
        <NoDataAvailable text="No permissions found" />
      )}
    </Flex>
  );

  // Access paths first: they are how the identity obtains everything the
  // permissions section below then lists.
  return (
    <Stack h="full" spacing={4} w="full">
      <Box>
        <Accordion allowMultiple defaultIndex={[0]}>
          <MetaAccordionItem
            iconType={IconTypes.Permission}
            height="380px"
            header={
              <HStack flex={1} spacing={2}>
                <Box fontWeight={600}>
                  {isSnowflake
                    ? 'How this identity gets its access privileges'
                    : 'How this identity gets its access'}
                </Box>
                <Box>
                  {canAccess.isLoading ? <CustomSpinner /> : pathSummary}
                </Box>
              </HStack>
            }
            onClickDownload={() => {}}
            isLoading={canAccess.isLoading}
            rightHeader={() =>
              showExternalResources && hasExternal ? (
                <AccessScopeButtons
                  items={scopeItems}
                  scope={scope}
                  onChange={setScope}
                />
              ) : null
            }
            panel={
              <Table
                columns={accessPathColumns}
                data={scopedPaths}
                isLoading={canAccess.isLoading}
                onRowClick={selectPath}
                cursor="pointer"
                noDataMessage="No access paths found"
              />
            }
            iconBGColor="primary"
            onShowGraph={() => redirectToGraph('access', MetadataTabs.Access)}
            styles={{ item: { mt: 0 } }}
          />
        </Accordion>
      </Box>
      {showPermissionDetails && (
        <PermissionDetails
          isLoading={isLoadingPermissions}
          privilegeItems={privilegeItems}
          activePrivilege={activePrivilege}
          onSelectPrivilege={setSelectedPrivilege}
          panel={permissionPanel}
        />
      )}
    </Stack>
  );
};
